import { Environment, PerformanceMonitor } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { XR, createXRStore } from "@react-three/xr";
import { Suspense, useCallback, useEffect, useRef, type ReactNode, type RefObject } from "react";
import * as THREE from "three";
import { DEFAULT_PLACEMENT, WEBXR_FIXED_PLACEMENT, type PlacementPose } from "@/runtime/types";
import { isImmersiveMode, qualityPolicy, renderQualityForMode } from "@/scene/qualityPolicy";
import type { SceneStageProps } from "@/scene/types";
import { usePlacementGestures } from "@/scene/usePlacementGestures";
import { WebXRPlacement } from "@/scene/WebXRPlacement";
import { attachWebglLifecycle } from "@/scene/webglLifecycle";

const environmentTexture = `${import.meta.env.BASE_URL}assets/textures/potsdamer_platz_1k.hdr`;
const overlayRoot = document.getElementById("root") ?? document.body;

export const xrStore = createXRStore({
  offerSession: false,
  enterGrantedSession: false,
  emulate: false,
  controller: false,
  hand: false,
  gaze: false,
  transientPointer: false,
  screenInput: true,
  // The whole HUD stays in the DOM during the session, so critical controls
  // remain semantic, focusable and readable rather than becoming 3D panels.
  domOverlay: overlayRoot,
  hitTest: true,
  planeDetection: false,
  meshDetection: false,
  depthSensing: false,
  frameBufferScaling: "mid",
});

export function SceneStage(props: SceneStageProps) {
  const immersive = isImmersiveMode(props.mode);
  const policy = qualityPolicy(props.quality, immersive);
  const renderQuality = renderQualityForMode(props.quality, props.mode);
  return (
    <Canvas
      className="experience-canvas"
      camera={{ position: [0, 0.55, 4.7], fov: 42, near: 1, far: 30 }}
      dpr={policy.dpr}
      gl={{ alpha: true, antialias: policy.antialias, powerPreference: immersive ? "default" : "high-performance" }}
      shadows={policy.shadows}
      onCreated={({ gl }) => {
        gl.outputColorSpace = THREE.SRGBColorSpace;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.1;
        gl.setClearColor(0x000000, 0);
        gl.domElement.dataset.testid = "experience-canvas";
      }}
    >
      <XR store={xrStore}>
        <WebglLifecycle onLost={props.onWebglContextLost} />
        <SceneContents {...props} quality={renderQuality} />
      </XR>
    </Canvas>
  );
}

function SceneContents(props: SceneStageProps) {
  const policy = qualityPolicy(props.quality, isImmersiveMode(props.mode));
  const placementGesturesEnabled = useRef(true);
  const setPlacementGesturesEnabled = useCallback((enabled: boolean) => {
    placementGesturesEnabled.current = enabled;
  }, []);
  const placementPose = props.placement ?? (props.mode === "webxr" ? WEBXR_FIXED_PLACEMENT : null);
  const placementGroup = useRef<THREE.Group>(null);
  const resetPlacementRotation = useCallback(() => {
    if (placementPose) placementGroup.current?.quaternion.set(...placementPose.quaternion);
  }, [placementPose]);

  return (
    <>
      {/* {!isImmersiveMode(props.mode) ? <Environment preset="city" background={false} environmentIntensity={0.5} /> : null} */}
      {/* <Environment preset="city" background={false} environmentIntensity={0.5} /> */}
      <Suspense fallback={null}>
        <Environment files={environmentTexture} background={false} environmentIntensity={0.5} />
      </Suspense>
      {/* {props.mode === "non-ar" ? <color attach="background" args={["#030712"]} /> : null} */}
      <ViewerFraming framing={props.viewerFraming} enabled={!isImmersiveMode(props.mode)} />

      {/* Fog would eat a subject sitting 3-4m out; keep it off the viewer modes. */}
      {isImmersiveMode(props.mode) ? <fog attach="fog" args={["#050917", 6, 14]} /> : null}
      <ambientLight intensity={isImmersiveMode(props.mode) ? 0.9 : 1.5} color="#a9c2ff" />
      <directionalLight position={[3, 5, 2]} intensity={3.4} color="#d7f7ff" castShadow={policy.shadows !== false} />
      <pointLight position={[-3, 1, 2]} intensity={5} color="#2eeeff" distance={9} />
      <pointLight position={[3, -1, 1]} intensity={4} color="#9c5cff" distance={8} />
      {/* Front fill: without it, a dark anodised subject reads as a silhouette
          against an equally dark background in the non-AR viewer. */}
      {isImmersiveMode(props.mode)
        ? null
        : <pointLight position={[0, 3.2, 2.2]} intensity={22} color="#eaf6ff" distance={12} decay={1.6} />}

      {props.mode === "webxr" && props.placement === null ? (
        <WebXRPlacement onPlacement={props.onPlacement} onProgress={props.onPlacementProgress} />
      ) : null}

      {placementPose ? (
        <InteractivePlacement
          groupRef={placementGroup}
          pose={placementPose}
          gesturesEnabled={placementGesturesEnabled}
        >
          <group position={[0, props.mode === "webxr" ? props.arFloorOffsetY : 0, 0]}>
            <Suspense fallback={<LoadingGlyph />}>
              <props.Scene
                runtime={{
                  mode: props.mode,
                  placed: props.placement !== null,
                  placement: { position: placementPose.position, scale: placementPose.scale },
                }}
                quality={props.quality}
                reducedMotion={props.reducedMotion}
                resetEpoch={props.resetEpoch}
                controls={props.controls}
                onAction={props.onAction}
                setPlacementGesturesEnabled={setPlacementGesturesEnabled}
                resetPlacementRotation={resetPlacementRotation}
              />
            </Suspense>
          </group>
        </InteractivePlacement>
      ) : null}

      <PerformanceMonitor
        ms={750}
        iterations={3}
        threshold={0.65}
        bounds={() => [32, 48]}
        onDecline={() => {
          // In XR the compositor already caps us; re-tiering there just thrashes.
          if (!isImmersiveMode(props.mode)) props.onQualityChange(props.quality === "high" ? "medium" : "low");
        }}
        onIncline={() => {
          if (!isImmersiveMode(props.mode)) props.onQualityChange(props.quality === "low" ? "medium" : "high");
        }}
      />
    </>
  );
}

function InteractivePlacement({
  pose,
  children,
  gesturesEnabled,
  groupRef,
}: {
  pose: PlacementPose;
  children: ReactNode;
  gesturesEnabled: RefObject<boolean>;
  groupRef: RefObject<THREE.Group | null>;
}) {
  const group = usePlacementGestures(gesturesEnabled, pose, groupRef);
  return (
    <group ref={group} position={pose.position} quaternion={pose.quaternion} scale={pose.scale}>
      {children}
    </group>
  );
}

function LoadingGlyph() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, delta) => { if (ref.current) ref.current.rotation.z += delta * 1.5; });
  return (
    <mesh ref={ref}>
      <torusGeometry args={[0.25, 0.025, 12, 48]} />
      <meshBasicMaterial color="#58e9ff" />
    </mesh>
  );
}

/**
 * Points the viewer camera at the subject. Only outside XR: in a session the
 * headset or phone pose owns the camera and writing to it fights the runtime.
 */
function ViewerFraming({ framing, enabled }: { framing: SceneStageProps["viewerFraming"]; enabled: boolean }) {
  const camera = useThree((state) => state.camera);

  useEffect(() => {
    if (!enabled) return;
    camera.position.set(...framing.position);
    camera.lookAt(...framing.target);
    camera.updateMatrixWorld();
  }, [camera, enabled, framing]);

  return null;
}

function WebglLifecycle({ onLost }: { onLost: () => void }) {
  const { gl } = useThree();
  useEffect(() => attachWebglLifecycle(gl.domElement, { onLost }), [gl, onLost]);
  return null;
}

export { DEFAULT_PLACEMENT };
